import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import {connectionToNodes} from '@wandb/weave/core';
import React, {useCallback, useMemo} from 'react';

import {
  useCreateArtifactCollectionTagAssignmentsMutation,
  useDeleteArtifactCollectionTagAssignmentsMutation,
  useEntityTagsQuery,
} from '../../generated/graphql';
import {ENTITY_TAGS_QUERY} from '../../state/graphql/entityTagsQuery';
import * as GQLTypes from '../../types/graphql';
import {getErrMsgFromError, propagateErrorsContext} from '../../util/errors';
import {useIsProjectReadOnly} from '../Registries/common/hooks';
import {
  AddTagAssignmentButton,
  createAttributesString,
  mapGQLTags,
  matchTag,
  ModifiedTag,
  parseTag,
  TagAssignment,
  TagAttributes,
} from '../TagAssignment';
import {InstrumentedWaveLoader} from '../utility/InstrumentedLoader';
import {useArtifactTags} from './hooks';

export type TagListProps = {
  showButton: boolean;
  showColor: boolean;
  tagSize: 'small' | 'medium' | 'large';
  wrap?: boolean;
  showLoader?: boolean;
};

type ArtifactTagListProps = {
  artifactCollectionID?: string;
};

const ArtifactTagList: React.FC<
  ArtifactTagListProps & TagListProps
> = props => {
  const {
    tags,
    entityName,
    projectName,
    artifactCollectionName,
    loading,
    refetch,
  } = useArtifactTags(props.artifactCollectionID);

  const {
    data: entityData,
    loading: entityLoading,
    error: entityError,
  } = useEntityTagsQuery({
    variables: {
      entityName: entityName,
    },
    // entityTags are only needed while rendering the list of tags
    // when a user tries to add a new tag. So the query result is not
    // needed if we're rendering the list without the 'add' button
    skip: !entityName || !props.showButton,
  });
  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(entityName ?? '', projectName ?? '');

  const entity = entityData?.entity;
  const allTags = useMemo(() => {
    const rawTags =
      entity?.tagCategories?.flatMap(tc => connectionToNodes(tc.tags)) ?? [];
    return mapGQLTags(rawTags);
  }, [entity]);

  if (loading || entityLoading || isProjectReadOnlyLoading) {
    if (props.showLoader) {
      return (
        <TailwindContents>
          <div className="flex h-full items-center">
            <InstrumentedWaveLoader
              name="artifact-collection-tag-list"
              size="small"
            />
          </div>
        </TailwindContents>
      );
    } else {
      return <></>;
    }
  }

  // We don't want to display the error message in case of the
  // entity query failing as they only list out existing tags on the collection
  if (entityError) {
    const errMsg = getErrMsgFromError(entityError);
    console.error(`Error retrieving entity tags; Msg: ${errMsg}`);
  }

  return (
    <ArtifactCollectionTagListInner
      {...props}
      refresh={refetch}
      entityName={entityName}
      projectName={projectName}
      artifactCollectionName={artifactCollectionName}
      currentTags={tags}
      allTags={allTags}
      showButton={props.showButton && !readOnly}
    />
  );
};

type ArtifactCollectionTagListInnerProps = {
  refresh: () => void;
  entityName: string;
  projectName: string;
  artifactCollectionName: string;
  currentTags: ModifiedTag[];
  allTags: ModifiedTag[];
};

export const ArtifactCollectionTagListInner: React.FC<
  ArtifactCollectionTagListInnerProps & TagListProps
> = props => {
  const [addTagAssignment] = useCreateArtifactCollectionTagAssignmentsMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: ENTITY_TAGS_QUERY,
        variables: {entityName: props.entityName},
      },
    ],
  });

  const createTagAssignment = useCallback(
    async (tagInput: GQLTypes.Tag) => {
      try {
        const tagStr = tagInput.name;
        if (props.currentTags.some((t: ModifiedTag) => matchTag(t, tagStr))) {
          return Promise.resolve();
        }

        // Parse input string into tag and category
        const [tagCategoryName, tagName] = parseTag(tagStr);

        // Stringify Attributes
        const tagAttributes: TagAttributes = {
          color: tagInput.colorIndex,
          icon: tagInput.icon,
        };
        const attributesStr = createAttributesString(tagAttributes);
        await addTagAssignment({
          variables: {
            entityName: props.entityName,
            projectName: props.projectName,
            artifactCollectionName: props.artifactCollectionName,
            tags: [
              {
                tagCategoryName,
                tagName,
                attributes: attributesStr,
              },
            ],
          },
        });
        props.refresh();
      } catch (err) {
        console.error(err);
        toast(
          'Something went wrong when trying to create this tag. Please check for special characters.'
        );
      }
      return;
    },
    [addTagAssignment, props]
  );

  const [removeTagAssignment] =
    useDeleteArtifactCollectionTagAssignmentsMutation({
      context: propagateErrorsContext(),
      refetchQueries: [
        {
          query: ENTITY_TAGS_QUERY,
          variables: {entityName: props.entityName},
        },
      ],
    });

  const deleteTagAssignment = useCallback(
    async (tag: ModifiedTag) => {
      try {
        await removeTagAssignment({
          variables: {
            entityName: props.entityName,
            projectName: props.projectName,
            artifactCollectionName: props.artifactCollectionName,
            tags: [
              {
                tagCategoryName: tag.tagCategoryName,
                tagName: tag.name,
              },
            ],
          },
        });
        props.refresh();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to delete this tag.');
      }
      return;
    },
    [removeTagAssignment, props]
  );

  return (
    <Tailwind>
      <div className={`flex ${props.wrap ? 'flex-wrap gap-y-5' : ''}`}>
        {props.currentTags.map(tag => {
          return (
            <TagAssignment
              key={tag.tagCategoryName + tag.name}
              tag={tag}
              showColor={props.showColor}
              size={props.tagSize}
              canDelete={props.showButton}
              onDelete={() => deleteTagAssignment(tag)}
            />
          );
        })}
        {props.showButton && (
          <AddTagAssignmentButton
            assignedTags={props.currentTags}
            availableTags={props.allTags}
            onCreate={createTagAssignment}
          />
        )}
      </div>
    </Tailwind>
  );
};

export default ArtifactTagList;
